/*
 * Your use of this software is governed by the terms of the Software License
 * and Support Agreement entered into between Gene42 Inc. and the licensing institution.
 *
 * To request a copy of this agreement, please email info@gene42.com.
 * Copyright (c) 2016, Gene42 Inc. All Rights Reserved.
 */

/**
 * PTV2-1586 — pregnancy dating math. Pure, dependency-free, and the single implementation shared by
 * the engine (node label + person model) and the React node-editor field.
 *
 * Dating is ONE stored anchor date plus the basis on which it was asserted. LMP and EDD are two
 * representations of that one anchor and interconvert by Naegele's rule (EDD = LMP + 280 days); the
 * basis matters because an ultrasound-revised EDD is a new anchor that no longer equals LMP + 280,
 * so the LMP shown for it is a back-derived approximation.
 *
 * Gestational age is never stored as a drifting number. A clinician may still enter one, but only
 * together with the date it was measured on (the as-of date): that pair is a fixed fact, converted to
 * an LMP anchor on entry, with basis GA and the as-of date kept as provenance. The GA shown afterwards
 * is always derived from the anchor against a reference date.
 */

/** Naegele's rule: a term pregnancy is 40 completed weeks from the last menstrual period. */
export const DAYS_LMP_TO_EDD = 280;

export const PREGNANCY_DATING_BASIS = { LMP: 'LMP', EDD: 'EDD', GA: 'GA' };

/** Largest enterable gestational age, in days (44w 6d). */
export const MAX_GESTATIONAL_AGE_DAYS = 44 * 7 + 6;

/** Gestational ages above this many days (42w 0d) are accepted with a post-term warning. */
export const POST_TERM_WARNING_DAYS = 42 * 7;

var MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Convert a `{year, month, day}` simple date (month is 1-based, as PedigreeDate emits) to a UTC
 * Date. Returns null unless all three parts are present — dating arithmetic needs a whole day, and a
 * year-only "date" would silently become January 1st.
 *
 * @param {{year: number, month: number, day: number}|null|undefined} simple
 * @returns {Date|null}
 */
export function toUTCDate(simple) {
    if (!simple) {
        return null;
    }
    var year = parseInt(simple.year, 10);
    var month = parseInt(simple.month, 10);
    var day = parseInt(simple.day, 10);
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return null;
    }
    return new Date(Date.UTC(year, month - 1, day));
}

/**
 * @param {Date|null|undefined} date
 * @returns {{year: number, month: number, day: number}|null}
 */
export function toSimpleDate(date) {
    if (!date || isNaN(date.getTime())) {
        return null;
    }
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

/**
 * @param {Date} date
 * @param {number} days
 * @returns {Date}
 */
export function shiftDays(date, days) {
    return new Date(date.getTime() + days * MS_PER_DAY);
}

/**
 * Normalize an arbitrary dating value into `{anchor, basis, asOf}`, or null when there is no usable
 * anchor. An anchor without a recognised basis is read as an LMP — that is what every legacy
 * representation (stored LMP, authored gestational age, estimated conception date) reduced to.
 *
 * `asOf` is the date a GA-basis value was measured on, and is null for any other basis. A GA basis
 * without a usable as-of date degrades to LMP: its anchor already is the LMP, only the provenance is
 * lost.
 *
 * @param {{anchor: Object|null, basis: string|null, asOf?: Object|null}|null|undefined} dating
 * @returns {{anchor: {year: number, month: number, day: number}, basis: string, asOf: Object|null}|null}
 */
export function normalizeDating(dating) {
    if (!dating) {
        return null;
    }
    var anchor = toSimpleDate(toUTCDate(dating.anchor));
    if (!anchor) {
        return null;
    }
    if (dating.basis === PREGNANCY_DATING_BASIS.GA) {
        var asOf = toSimpleDate(toUTCDate(dating.asOf));
        if (asOf) {
            return { anchor: anchor, basis: PREGNANCY_DATING_BASIS.GA, asOf: asOf };
        }
    }
    return {
        anchor: anchor,
        basis: dating.basis === PREGNANCY_DATING_BASIS.EDD
            ? PREGNANCY_DATING_BASIS.EDD
            : PREGNANCY_DATING_BASIS.LMP,
        asOf: null
    };
}

/**
 * The dating a clinician asserts by entering a gestational age measured on `asOf` (e.g. a scan date):
 * an LMP anchor of asOf − (weeks × 7 + days), with basis GA. Null when the age is outside
 * 0w 0d – 44w 6d, days is not 0–6, or asOf is not a whole date.
 *
 * @param {{weeks: number, days: number}} ga
 * @param {{year: number, month: number, day: number}} asOf
 * @returns {{anchor: Object, basis: string, asOf: Object}|null}
 */
export function datingFromGestationalAge(ga, asOf) {
    var asOfUTC = toUTCDate(asOf);
    var totalDays = gestationalAgeDays(ga);
    if (!asOfUTC || totalDays === null || totalDays > MAX_GESTATIONAL_AGE_DAYS) {
        return null;
    }
    return {
        anchor: toSimpleDate(shiftDays(asOfUTC, -totalDays)),
        basis: PREGNANCY_DATING_BASIS.GA,
        asOf: toSimpleDate(asOfUTC)
    };
}

/**
 * Total days of a `{weeks, days}` gestational age, or null unless both parts are non-negative
 * integers and days is 0–6.
 *
 * @param {{weeks: number, days: number}|null|undefined} ga
 * @returns {number|null}
 */
export function gestationalAgeDays(ga) {
    if (!ga || !Number.isInteger(ga.weeks) || !Number.isInteger(ga.days)) {
        return null;
    }
    if (ga.weeks < 0 || ga.days < 0 || ga.days > 6) {
        return null;
    }
    return ga.weeks * 7 + ga.days;
}

/**
 * The gestational age the clinician asserted on a GA-basis dating, i.e. the GA on its as-of date.
 * Null for any other basis.
 *
 * @param {{anchor: Object|null, basis: string|null, asOf?: Object|null}|null} dating
 * @returns {{weeks: number, days: number}|null}
 */
export function assertedGestationalAgeOf(dating) {
    var normalized = normalizeDating(dating);
    if (!normalized || normalized.basis !== PREGNANCY_DATING_BASIS.GA) {
        return null;
    }
    var asOf = toUTCDate(normalized.asOf);
    return gestationalAgeOf(normalized, new Date(asOf.getUTCFullYear(), asOf.getUTCMonth(), asOf.getUTCDate()));
}

/**
 * The last menstrual period: the anchor itself on an LMP or GA basis, else the Naegele
 * back-derivation from the asserted due date.
 *
 * @param {{anchor: Object|null, basis: string|null}|null} dating
 * @returns {{year: number, month: number, day: number}|null}
 */
export function lmpOf(dating) {
    var normalized = normalizeDating(dating);
    if (!normalized) {
        return null;
    }
    if (normalized.basis !== PREGNANCY_DATING_BASIS.EDD) {
        return normalized.anchor;
    }
    return toSimpleDate(shiftDays(toUTCDate(normalized.anchor), -DAYS_LMP_TO_EDD));
}

/**
 * The estimated due date: the anchor itself on an EDD basis, else LMP + 280 days (a GA basis
 * anchor is an LMP).
 *
 * @param {{anchor: Object|null, basis: string|null}|null} dating
 * @returns {{year: number, month: number, day: number}|null}
 */
export function eddOf(dating) {
    var normalized = normalizeDating(dating);
    if (!normalized) {
        return null;
    }
    if (normalized.basis === PREGNANCY_DATING_BASIS.EDD) {
        return normalized.anchor;
    }
    return toSimpleDate(shiftDays(toUTCDate(normalized.anchor), DAYS_LMP_TO_EDD));
}

/**
 * Gestational age in completed weeks + days, measured from the LMP to the reference date. Null when
 * there is no anchor, or when the anchor puts the reference date before conception — a future LMP is
 * a data-entry error, not a negative gestational age.
 *
 * @param {{anchor: Object|null, basis: string|null}|null} dating
 * @param {Date} [referenceDate] defaults to today
 * @returns {{weeks: number, days: number}|null}
 */
export function gestationalAgeOf(dating, referenceDate) {
    var lmp = toUTCDate(lmpOf(dating));
    if (!lmp) {
        return null;
    }
    var reference = referenceDate || new Date();
    var referenceUTC = Date.UTC(reference.getFullYear(), reference.getMonth(), reference.getDate());
    var totalDays = Math.floor((referenceUTC - lmp.getTime()) / MS_PER_DAY);
    if (totalDays < 0) {
        return null;
    }
    return { weeks: Math.floor(totalDays / 7), days: totalDays % 7 };
}

/**
 * @param {{weeks: number, days: number}|null} ga
 * @returns {string|null} e.g. "18w 5d", "18w", "5d"
 */
export function formatGestationalAge(ga) {
    if (!ga) {
        return null;
    }
    if (ga.weeks === 0 && ga.days === 0) {
        return '0w';
    }
    if (ga.days === 0) {
        return ga.weeks + 'w';
    }
    if (ga.weeks === 0) {
        return ga.days + 'd';
    }
    return ga.weeks + 'w ' + ga.days + 'd';
}

/**
 * ISO `YYYY-MM-DD`, the format the date inputs and the demographics wire both use.
 *
 * @param {{year: number, month: number, day: number}|null} simple
 * @returns {string|null}
 */
export function formatDatingDate(simple) {
    if (!simple) {
        return null;
    }
    var pad = function(n) {
        return n < 10 ? '0' + n : '' + n;
    };
    return simple.year + '-' + pad(simple.month) + '-' + pad(simple.day);
}

/**
 * Parse an ISO `YYYY-MM-DD` string into a simple date. Returns null for anything else — a partial
 * date cannot anchor dating.
 *
 * @param {string|null|undefined} iso
 * @returns {{year: number, month: number, day: number}|null}
 */
export function parseDatingDate(iso) {
    if (typeof iso !== 'string') {
        return null;
    }
    var parsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
    if (!parsed) {
        return null;
    }
    var simple = {
        year: parseInt(parsed[1], 10),
        month: parseInt(parsed[2], 10),
        day: parseInt(parsed[3], 10)
    };
    // Reject calendar-invalid values (e.g. 2026-02-31), which Date.UTC would silently roll over.
    var roundTrip = toSimpleDate(toUTCDate(simple));
    if (!roundTrip || roundTrip.year !== simple.year || roundTrip.month !== simple.month
        || roundTrip.day !== simple.day) {
        return null;
    }
    return simple;
}

/**
 * Read pregnancy dating out of a pre-PTV2-1586 node-properties object. Those pedigrees carried an
 * estimated conception date, a bare last menstrual period, or an authored gestational age in weeks;
 * all three reduce to an LMP-basis anchor.
 *
 * The gestational-age form is the lossy one: weeks were authored against the day they were typed, so
 * the anchor can only be recovered relative to `referenceDate`. That is the drift this model exists
 * to remove — the conversion happens once, on load, and the anchor is stable from then on.
 *
 * @param {Object} info legacy node properties
 * @param {Date} [referenceDate] defaults to today
 * @returns {{anchor: Object, basis: string}|null}
 */
export function legacyDatingFrom(info, referenceDate) {
    if (!info) {
        return null;
    }
    var asLmpAnchor = function(simple) {
        return simple ? { anchor: simple, basis: PREGNANCY_DATING_BASIS.LMP } : null;
    };
    // An estimated conception date sits ~14 days after the LMP.
    var ecd = toUTCDate(info.estimatedConceptionDate);
    if (ecd) {
        return asLmpAnchor(toSimpleDate(shiftDays(ecd, -14)));
    }
    var lmp = toUTCDate(info.lastMenstrualPeriod);
    if (lmp) {
        return asLmpAnchor(toSimpleDate(lmp));
    }
    var weeks = parseInt(info.gestationAge, 10);
    if (!isNaN(weeks) && weeks > 0) {
        var reference = referenceDate || new Date();
        var referenceUTC = new Date(
            Date.UTC(reference.getFullYear(), reference.getMonth(), reference.getDate())
        );
        return asLmpAnchor(toSimpleDate(shiftDays(referenceUTC, -weeks * 7)));
    }
    return null;
}
